import WhitePlusIcon from "../../images/deliveries/whitePlusIcon.svg";
import { EditIcon } from "../../images/deliveries/deliveriesModals/editIcon.jsx";
import { ActionBtn } from "../../components/buttons/actionBtn/ActionBtn.jsx";
import { BaseTable } from "../../components/UI/baseTable/BaseTable.jsx";
import { DeleteIcon } from "../../images/deliveries/deliveriesModals/deleteIcon.jsx";
import { PageWrapper } from "../../components/UI/pageWrapper/PageWrapper.jsx";
import { fetchCustomers } from "../../redux/customer/thunks/getCustomerThunk.js";
import { setSearchQuery } from "../../redux/misc/slices/searchSlice.js";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations.jsx";
import { NavToggleButton } from "../../components/buttons/navToggleBtn/NavToggleBtn.jsx";
import { fetchDepartments } from "../../redux/department/thunks/getDepartmentThunk.js";
import { resetCustomerForm } from "../../redux/forms/customerForm.js";
import { resetDepartmentForm } from "../../redux/forms/departmentForm.js";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  resetCustomersError,
  resetCustomersState,
} from "../../redux/customer/slices/getCustomerSlice.js";
import {
  resetDepartments,
  resetDepartmentsState,
} from "../../redux/department/slices/getDepartmentSlice.js";
import {
  toggleAddNewCustomerAddress,
  setIsEditMode,
  setIsCustomerActive,
  toggleAddLaundry,
  toggleDeleteCustomerAddressModal,
  setSelectedDataForEdit,
  setIsFetchCurrentAddress,
} from "../../redux/globalFunctions/globalFnSlice.js";

export function Addresses() {
  const dispatch = useDispatch();
  const { isCustomerActive } = useSelector((state) => state.global);
  const { t } = useTranslations();
  const addressesPage = t.addresses;

  // Variables for redux states.
  const searchQuery = useSelector((state) => state.search.query);

  // Customer state variables.
  const customersState = useSelector((state) => state.customers);
  const updateCustomerState = useSelector((state) => state.updateCustomer);
  const createCustomerState = useSelector((state) => state.createCustomer);
  const deleteCustomerState = useSelector((state) => state.deleteCustomer);

  // Department state variables.
  const departmentsState = useSelector((state) => state.departments);
  const updateDepartmentState = useSelector((state) => state.updateDepartment);
  const createDepartmentState = useSelector((state) => state.createDepartment);
  const deleteDepartmentState = useSelector((state) => state.deleteDepartment);

  const [customerFilters, setCustomerFilters] = useState({ customer: "" });
  const [departmentFilters, setDepartmentFilters] = useState({
    department_name: "",
  });

  const [isFiltered, setIsFiltered] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Getting total pages count functions.
  const totalCustomerPagesCount = Math.ceil(customersState.total_count / 10);
  const departmentTotalPagesCount = Math.ceil(
    departmentsState.total_count / 10
  );

  const filteredCustomerPages = Math.ceil(customersState.filtered_count / 10);
  const filteredDepartmentPages = Math.ceil(
    departmentsState.filtered_count / 10
  );

  const isStatusSuccess =
    updateCustomerState.status === "success" ||
    deleteCustomerState.status === "success" ||
    updateDepartmentState.status === "success" ||
    deleteDepartmentState.status === "success";

  // Initial fetch and append data to page.
  useEffect(() => {
    if (isCustomerActive) {
      dispatch(fetchCustomers({ filters: customerFilters, page: currentPage }));
    } else if (!isCustomerActive) {
      dispatch(
        fetchDepartments({ filters: departmentFilters, page: currentPage })
      );
    }
  }, [
    dispatch,
    isCustomerActive,
    customerFilters,
    departmentFilters,
    currentPage,
  ]);

  // Check customer-related statuses.
  useEffect(() => {
    if (isCustomerActive) {
      if (
        createCustomerState.status === "success" &&
        customersState.total_count % 10 === 0
      ) {
        setCurrentPage(totalCustomerPagesCount + 1);
        dispatch(
          fetchCustomers({
            filters: customerFilters,
            page: totalCustomerPagesCount + 1,
          })
        );
      } else if (createCustomerState.status === "success") {
        setCurrentPage(totalCustomerPagesCount);
        dispatch(
          fetchCustomers({
            filters: customerFilters,
            page: totalCustomerPagesCount,
          })
        );
      } else if (
        customersState.customers.length === 0 &&
        currentPage === 1 &&
        createCustomerState.status === "success"
      ) {
        dispatch(fetchCustomers({ filters: customerFilters, page: 1 }));
      }
    }
  }, [createCustomerState.status]);

  // If deleted the last result on page, refetch results one page back.
  useEffect(() => {
    if (customersState.status === "error" && currentPage !== 1) {
      setCurrentPage((prev) => prev - 1);
      dispatch(
        fetchCustomers({ filters: customerFilters, page: currentPage - 1 })
      );

      setTimeout(() => {
        dispatch(resetCustomersError());
      }, 100);
    }
  }, [customersState.status, currentPage, customerFilters, dispatch]);

  // If deleted the last result on page, refetch results one page back.
  useEffect(() => {
    if (
      !isCustomerActive &&
      departmentsState.status === "error" &&
      currentPage !== 1
    ) {
      setCurrentPage((prev) => prev - 1);
      dispatch(
        fetchDepartments({ filters: departmentFilters, page: currentPage - 1 })
      );

      setTimeout(() => {
        dispatch(resetDepartmentsState());
      }, 100);
    }
  }, [
    isCustomerActive,
    departmentsState.status,
    currentPage,
    departmentFilters,
    dispatch,
  ]);

  // Check department-related statuses.
  useEffect(() => {
    if (!isCustomerActive) {
      if (
        createDepartmentState.status === "success" &&
        departmentsState.total_count % 10 === 0
      ) {
        setCurrentPage(departmentTotalPagesCount + 1);
        dispatch(
          fetchDepartments({
            filters: departmentFilters,
            page: departmentTotalPagesCount + 1,
          })
        );
      } else if (createDepartmentState.status === "success") {
        setCurrentPage(departmentTotalPagesCount);
        dispatch(
          fetchDepartments({
            filters: departmentFilters,
            page: departmentTotalPagesCount,
          })
        );
      } else if (
        departmentsState.departments.length === 0 &&
        currentPage === 1 &&
        createDepartmentState.status === "success"
      ) {
        dispatch(fetchDepartments({ filters: departmentFilters, page: 1 }));
      }
    }
  }, [createDepartmentState.status]);

  // If an update or delete function is executed, refresh the results on the page.
  useEffect(() => {
    // Customer related statuses.
    if (isStatusSuccess && isCustomerActive) {
      dispatch(fetchCustomers({ filters: customerFilters, page: currentPage }));
    }

    // Department related statuses.
    if (isStatusSuccess && !isCustomerActive) {
      dispatch(
        fetchDepartments({ filters: departmentFilters, page: currentPage })
      );
    }
  }, [
    updateCustomerState.status,
    deleteCustomerState.status,
    updateDepartmentState.status,
    deleteDepartmentState.status,
    customerFilters,
    departmentFilters,
    isCustomerActive,
    currentPage,
  ]);

  // Update filters based on search query changes
  useEffect(() => {
    if (searchQuery !== "") {
      if (isCustomerActive) {
        setCustomerFilters({
          ...customerFilters,
          customer: searchQuery,
        });
        setCurrentPage(1);
        setDepartmentFilters({ department_name: "" });
        setIsFiltered(true);
      } else if (!isCustomerActive) {
        setDepartmentFilters({
          ...departmentFilters,
          department_name: searchQuery,
        });
        setCurrentPage(1);
        setCustomerFilters({ customer: "" });
        setIsFiltered(true);
      }
    } else {
      // If search query is empty, reset both filters
      setCustomerFilters({ customer: "" });
      setDepartmentFilters({ department_name: "" });
    }
  }, [searchQuery, isCustomerActive]);

  // Function for reseting the filters and states on tab change.
  useEffect(() => {
    dispatch(setSearchQuery(""));
    setCustomerFilters({ customer: "" });
    setDepartmentFilters({ department_name: "" });
    setCurrentPage(1);
  }, [isCustomerActive]);

  function handleClick() {
    dispatch(setIsEditMode(false));
    if (isCustomerActive) {
      dispatch(toggleAddNewCustomerAddress());
    } else {
      dispatch(toggleAddLaundry());
    }
  }

  const data = isCustomerActive
    ? customersState.customers
    : departmentsState.departments;

  function handleCustomersClick() {
    dispatch(setIsCustomerActive(true));
    setCurrentPage(1);
    dispatch(resetDepartments());
    dispatch(setIsFetchCurrentAddress(false));
  }

  function handleDepartmentsClick() {
    dispatch(setIsCustomerActive(false));
    setCurrentPage(1);
    dispatch(resetCustomersState());
    dispatch(setIsFetchCurrentAddress(false));
  }

  const columns = [
    {
      label: isCustomerActive
        ? addressesPage.table.customer.customer
        : addressesPage.table.department.department,
    },
    { label: addressesPage.table.address },
    { label: addressesPage.table.phone },
    { label: addressesPage.table.edit },
    { label: addressesPage.table.delete },
  ];

  const navButtons = [
    <NavToggleButton
      isActive={isCustomerActive}
      onClick={handleCustomersClick}
      text={addressesPage.customerBtn}
    />,
    <NavToggleButton
      isActive={!isCustomerActive}
      onClick={handleDepartmentsClick}
      text={addressesPage.laundryBtn}
    />,
  ];

  const headerButtons = [
    <ActionBtn
      onClick={handleClick}
      text={addressesPage.newAddressBtn}
      imgSrc={WhitePlusIcon}
      height={"42px"}
    />,
  ];

  function handleEditClick(data) {
    dispatch(setSelectedDataForEdit(data));
    dispatch(setIsEditMode(true));
    if (isCustomerActive) {
      dispatch(resetCustomerForm());
      dispatch(toggleAddNewCustomerAddress(data));
    } else {
      dispatch(resetDepartmentForm());
      dispatch(toggleAddLaundry(data));
    }
  }

  return (
    <PageWrapper navButtons={navButtons} buttons={headerButtons}>
      <BaseTable
        status={
          isCustomerActive ? customersState.status : departmentsState.status
        }
        resultsContainerHeight={"calc(100vh - 300px)"}
        errorMsg={addressesPage.errorFetchingData}
        noItemsMsg={addressesPage.errorFetchingData}
        items={data}
        columns={columns}
        columnAndRowWidth={"200px"}
        pagination={true}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={
          isCustomerActive ? totalCustomerPagesCount : departmentTotalPagesCount
        }
        totalFilteredPages={
          isCustomerActive ? filteredCustomerPages : filteredDepartmentPages
        }
        isFiltered={isFiltered}
        rows={
          data &&
          data?.map((item) => [
            isCustomerActive ? item.c_name : item.d_name,
            isCustomerActive ? item.c_address : item.d_address,
            isCustomerActive ? item.c_phonenumber : item.d_phonenumber,
            <EditIcon onClick={() => handleEditClick(item)} />,
            <DeleteIcon
              onClick={() => dispatch(toggleDeleteCustomerAddressModal(item))}
            />,
          ])
        }
      />
    </PageWrapper>
  );
}
